import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState } from "react";
import { useAuth } from "@/app";
import { Button, Modal, toast } from "@/shared/ui";
import { getApiErrorCode } from "@/shared/lib/apiError";
import { SUBSCRIPTION_ERROR, describeMySubscription, getSubscriptionErrorMessage, } from "@/shared/lib/subscriptionView";
import { useMySubscription } from "../../model/useMySubscription";
import styles from "./MySubscriptionBlock.module.scss";
/**
 * Блок «Моя подписка» на `/balance` (и в BalancePage, и в пейволе; веб и
 * миниапп). Показывает состояние подписки и даёт её отменить: отмена отключает
 * автопродление, доступ сохраняется до конца оплаченного периода. Подписки нет
 * (или пользователь — гость) → ничего не рендерит.
 *
 * Тексты и решение «показывать ли кнопку» — в `describeMySubscription`.
 */
export const MySubscriptionBlock = ({ language, className, }) => {
    const ru = language === "ru";
    const { refreshUser } = useAuth();
    const { subscription, isCancelling, cancel, reload } = useMySubscription({
        refreshOnMount: true,
    });
    const [confirmOpened, setConfirmOpened] = useState(false);
    const cancellingRef = useRef(false);
    if (!subscription)
        return null;
    const view = describeMySubscription(subscription, language);
    const handleCancel = async () => {
        // Второй клик, пока запрос в пути: ни второго запроса, ни второго тоста.
        if (cancellingRef.current)
            return;
        cancellingRef.current = true;
        try {
            await cancel();
            setConfirmOpened(false);
            toast.success(ru ? "Подписка отменена" : "Subscription cancelled");
            // В профиле тоже есть флаги подписки (is_autorenewal_enabled) — обновим.
            void refreshUser().catch(() => undefined);
        }
        catch (error) {
            // Отмена не прошла: состояние блока не трогаем (стор меняется только по
            // успешному ответу), модалка остаётся открытой — можно повторить.
            console.error("Ошибка отмены подписки:", error);
            const code = getApiErrorCode(error);
            // Подписки уже нет (истекла, пока блок был открыт) — перечитываем, блок
            // сам исчезнет.
            if (code === SUBSCRIPTION_ERROR.notFound) {
                setConfirmOpened(false);
                void reload();
            }
            toast.error(getSubscriptionErrorMessage(code, language) ??
                (ru
                    ? "Не удалось отменить подписку. Попробуйте ещё раз."
                    : "Couldn't cancel the subscription. Please try again."));
        }
        finally {
            cancellingRef.current = false;
        }
    };
    return (_jsxs("section", { className: `${styles.block} ${className ?? ""}`.trim(), "aria-label": ru ? "Моя подписка" : "My subscription", "data-state": view.state, children: [_jsxs("div", { className: styles.text, children: [_jsx("span", { className: styles.label, children: ru ? "Моя подписка" : "My subscription" }), _jsxs("span", { className: styles.headline, children: [_jsx("span", { className: `${styles.dot} ${view.state === "cancelled" ? styles.dotOff : ""}`.trim(), "aria-hidden": "true" }), view.headline] }), view.details && _jsx("span", { className: styles.details, children: view.details })] }), view.canCancel && (_jsx(Button, { variant: "outline", size: "sm", className: styles.cancel, onClick: () => setConfirmOpened(true), disabled: isCancelling, children: ru ? "Отменить подписку" : "Cancel subscription" })), _jsxs(Modal, { opened: confirmOpened, onClose: isCancelling ? () => { } : () => setConfirmOpened(false), size: "sm", title: ru ? "Отменить подписку?" : "Cancel subscription?", closeOnBackdropClick: !isCancelling, closeOnEscape: !isCancelling, children: [_jsx("p", { className: styles.confirmText, children: view.cancelConfirm }), _jsxs("div", { className: styles.confirmActions, children: [_jsx(Button, { variant: "ghost", size: "md", "data-autofocus": true, onClick: () => setConfirmOpened(false), disabled: isCancelling, children: ru ? "Не отменять" : "Keep subscription" }), _jsx(Button, { variant: "danger", size: "md", onClick: () => void handleCancel(), loading: isCancelling, children: ru ? "Отменить подписку" : "Cancel subscription" })] })] })] }));
};
